import React, { useCallback, useEffect, useState } from 'react';
import { Popover, Spin } from 'antd';
import { CheckOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';

interface ConstraintItem {
    name: string;
    label: string;
}

interface Props {
    items: ConstraintItem[];    // all available constraints to choose from
    selectedNames: string[];  // already selected constraints
    onApply: (selectedNames: string[]) => void;  // called with the full updated selection when the user confirms
    disabled?: boolean;  // whether the selector is disabled
    loading?: boolean;  // whether the selector is loading
}

const ConstraintSelector: React.FC<Props> = ({ items, selectedNames, onApply, disabled = false, loading = false }) => {
    const { labels } = useTpoGenConfig();
    const selectorLabels = labels.optimizePlan.selector;
    const [open, setOpen] = useState(false);
    const [tempSelected, setTempSelected] = useState<Set<string>>(new Set(selectedNames));
    const [search, setSearch] = useState('');

    useEffect(() => {
        setTempSelected(new Set(selectedNames));
    }, [selectedNames, open]);

    const isDisabled = disabled || loading;
    const allSelected = items.length > 0 && items.every((item) => tempSelected.has(item.name));

    const toggleSelectAll = useCallback(() => {
        setTempSelected(allSelected ? new Set() : new Set(items.map((i) => i.name)));
    }, [allSelected, items]);

    const toggleItem = useCallback((name: string) => {
        setTempSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    }, []);

    const handleApply = useCallback(() => {
        onApply(Array.from(tempSelected));
        setOpen(false);
        setSearch('');
    }, [tempSelected, onApply]);

    const handleCancel = useCallback(() => {
        setTempSelected(new Set(selectedNames));
        setOpen(false);
        setSearch('');
    }, [selectedNames]);

    const filtered = search ? items.filter((i) => i.label.toLowerCase().includes(search.toLowerCase())) : items;

    const triggerButton = (
        <button
            disabled={isDisabled}
            onClick={() => !isDisabled && setOpen((v) => !v)}
            className={`flex items-center gap-1.5 text-sm transition-colors ${
                isDisabled
                    ? 'text-[rgba(0,0,0,0.25)] cursor-not-allowed'
                    : 'text-[rgba(0,0,0,0.65)] hover:text-[#44338a] cursor-pointer'
            }`}
        >
            {loading ? <Spin size="small" /> : <PlusCircleOutlined />}
            <span>{selectorLabels.buttonLabel}</span>
        </button>
    );

    if (isDisabled) return triggerButton;

    const dropdownContent = (
        <div className="w-[300px]">
            {/* Select / Unselect all */}
            <div className="px-1 pb-1 flex justify-end">
                <button onClick={toggleSelectAll} className="text-xs text-[#44338a] hover:underline leading-none">
                    {allSelected ? selectorLabels.unselectAll : selectorLabels.selectAll}
                </button>
            </div>

            {/* Search */}
            <div className="relative pt-1 pb-2">
                <SearchOutlined className="absolute left-[10px] top-[40%] -translate-y-[30%] text-[#bfbfbf] pointer-events-none" />
                <input
                    type="text"
                    placeholder={selectorLabels.searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                    className="w-full pl-7 pr-3 py-1.5 text-sm border  rounded bg-[#0000000a] focus:outline-none focus:border-[#44338a]"
                />
            </div>

            {/* Item list */}
            <div className="max-h-48 overflow-y-auto px-0">
                {filtered.length > 0 ? (
                    filtered.map((item) => {
                        const selected = tempSelected.has(item.name);
                        return (
                            <div
                                key={item.name}
                                onClick={() => toggleItem(item.name)}
                                className={`flex items-center justify-between px-3 py-1.5 rounded mb-0.5 cursor-pointer text-sm select-none transition-colors ${
                                    selected
                                        ? 'bg-[#f0eeff] text-[rgba(0,0,0,0.88)]'
                                        : 'hover:bg-[#f5f5f5] text-[rgba(0,0,0,0.88)]'
                                }`}
                            >
                                <span>{item.label}</span>
                                {selected && <CheckOutlined className="!text-[#44338a] text-lg" />}
                            </div>
                        );
                    })
                ) : (
                    <div className="py-4 text-center text-sm text-[rgba(0,0,0,0.25)]">{selectorLabels.noResults}</div>
                )}
            </div>

            {/* Footer */}
            <div className="border-t border-[#f0f0f0] mt-1 pt-2 flex justify-end gap-2">
                <button
                    onClick={handleCancel}
                    className="px-3 py-1 text-sm border border-[#d9d9d9] rounded hover:border-[#44338a] hover:text-[#44338a] transition-colors"
                >
                    {selectorLabels.cancel}
                </button>
                <button
                    onClick={handleApply}
                    className="px-3 py-1 text-sm bg-[#44338a] text-white rounded hover:bg-[#3a2d7d] transition-colors"
                >
                    {selectorLabels.apply}
                </button>
            </div>
        </div>
    );

    return (
        <Popover
            content={dropdownContent}
            trigger="click"
            open={open}
            onOpenChange={(visible) => {
                if (!visible) handleCancel();
                else setOpen(true);
            }}
            placement="bottomRight"
            showArrow={false}
            overlayInnerStyle={{ padding: 0, borderRadius: 8, overflow: 'hidden' }}
        >
            {triggerButton}
        </Popover>
    );
};

export default React.memo(ConstraintSelector);
